// ─────────────────────────────────────────────────────────────────
// Ambient Sound Cues (Feb 2026)
// ─────────────────────────────────────────────────────────────────
// Tiny web-audio synth so we don't need to ship any audio files.
// Every cue is a short, gentle, kid-appropriate tone or arpeggio.
// A global mute (persisted in localStorage) silences everything —
// exposed via the <SoundToggle /> button so parents / kids can turn
// off sound at any time.
//
//   playCue("win")         — happy 3-note arpeggio (correct answer / win)
//   playCue("chest")       — bright ding (treasure / coin reveal)
//   playCue("reveal")      — soft rising blip (screen mount / page arrival)
//   playCue("tap")         — barely-there tick (option select)
//   playCue("badge")       — big 4-note flourish (badge / milestone)
//   playCue("wrong")       — gentle downward chirp (wrong answer)
//
// isMuted(), setMuted(bool), toggleMuted()

const MUTE_KEY = "mwq_sound_muted";
let ctxCache = null;

function ctx() {
  if (typeof window === "undefined") return null;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!ctxCache) ctxCache = new AudioCtx();
  return ctxCache;
}

let hostSilenced = false; // host lifecycle (suspend / audio interruption) — not a user preference, never persisted
export function setMutedTemporarily(v) {
  hostSilenced = !!v;
  if (hostSilenced) { try { ctxCache?.suspend?.(); } catch { /* ignore */ } } else { try { ctxCache?.resume?.(); } catch { /* ignore */ } }
}
export function isMuted() {
  if (hostSilenced) return true;
  try { return localStorage.getItem(MUTE_KEY) === "1"; }
  catch { return false; }
}
export function setMuted(v) {
  try { localStorage.setItem(MUTE_KEY, v ? "1" : "0"); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("mwq-mute-changed", { detail: !!v })); } catch { /* ignore */ }
}
export function toggleMuted() {
  const next = !isMuted();
  setMuted(next);
  return next;
}

// Emit a single note; combined into arpeggios for cues below.
function beep(freq, at = 0, dur = 0.18, gain = 0.06, wave = "sine") {
  const audio = ctx();
  if (!audio) return;
  const osc = audio.createOscillator();
  const g = audio.createGain();
  osc.type = wave;
  osc.frequency.value = freq;
  g.gain.value = 0;
  const t0 = audio.currentTime + at;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.015);
  g.gain.linearRampToValueAtTime(0, t0 + dur);
  osc.connect(g).connect(audio.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

// Note freqs (equal temperament) for the cues below.
const N = { C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880.0, C6: 1046.5, E6: 1318.5, G6: 1568.0, A4: 440.0, F5: 698.46 };

const CUES = {
  win:    () => { beep(N.C5, 0); beep(N.E5, 0.11); beep(N.G5, 0.22, 0.24); },
  chest:  () => { beep(N.G5, 0, 0.12); beep(N.C6, 0.09, 0.2, 0.07, "triangle"); },
  reveal: () => { beep(N.E5, 0, 0.16, 0.04); beep(N.G5, 0.09, 0.16, 0.04); },
  tap:    () => { beep(N.A5, 0, 0.06, 0.03, "triangle"); },
  badge:  () => {
    beep(N.C5, 0);
    beep(N.E5, 0.1);
    beep(N.G5, 0.2);
    beep(N.C6, 0.3, 0.32, 0.08);
  },
  wrong:  () => { beep(N.F5, 0, 0.14, 0.05); beep(N.A4, 0.12, 0.18, 0.05); },
  // Travel Mode — soft wooden "tok" on card taps. Only ever fired from a click handler (never on mount).
  travelTap: () => { beep(N.E5, 0, 0.07, 0.035, "triangle"); beep(N.G5, 0.04, 0.09, 0.025, "sine"); },
  // Dodge arcade — subtle metallic ring when a shield is earned in
  // the answer phase. Two-note "power up" ping in a bright triangle
  // wave so it reads as protective / defensive rather than
  // celebratory (celebration would over-reward correct answers).
  shield: () => {
    beep(N.G5, 0, 0.09, 0.05, "triangle");
    beep(N.C6, 0.06, 0.14, 0.06, "triangle");
  },
  // Dodge arcade — descending "whoosh" when an obstacle grazes the
  // ball. Sawtooth pitch-slide via two short beeps mimics a Doppler
  // shift so kids feel the near-miss. Kept short so a burst of hits
  // doesn't overwhelm.
  whoosh: () => {
    beep(N.C6, 0, 0.08, 0.06, "sawtooth");
    beep(N.G5, 0.05, 0.1, 0.05, "sawtooth");
    beep(N.E5, 0.11, 0.12, 0.04, "sawtooth");
  },
};

// Public helper — every call site can just say playCue("win").
export function playCue(name) {
  if (isMuted()) return;
  const fn = CUES[name];
  if (!fn) return;
  const audio = ctx();
  // Web Audio in browsers requires a user gesture before the first
  // sound. Resume the context lazily; if the browser hasn't received
  // a gesture yet, the play call is silently dropped.
  if (audio && audio.state === "suspended") {
    audio.resume().catch(() => { /* still no gesture — skip */ });
  }
  try { fn(); } catch { /* ignore */ }
}

// Auto-play a `reveal` cue whenever the route changes. Registered
// lazily by <SoundToggle /> so browsers only spin up an AudioContext
// after a user interacts with the toggle at least once.
export function playPageRevealSound() {
  playCue("reveal");
}
